import "@/styles/plus-menu.css";

import type { LucideIcon } from "lucide-react";
import { useTranslate } from "ra-core";
import {
  useEffect,
  useLayoutEffect,
  useRef,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
  type RefObject,
} from "react";
import { Link } from "react-router";

import { isEditableElement } from "@/hooks/useVisualViewport";
import { useTapThroughGuard } from "@/lib/tapThroughGuard";

import { hapticTick } from "../haptic";
import { playPop, playSquash } from "./squash";
import { staggerOrder } from "./staggerOrder";

export type PlusMenuItem = {
  label: string;
  Icon: LucideIcon;
} & ({ to: string } | { run: () => void });

export interface PlusMenuProps {
  id: string;
  open: boolean;
  onClose: () => void;
  /** Create actions: compact tiles, two per row. */
  primary: PlusMenuItem[];
  /** Navigation: compact chips, three per row, below the tiles. */
  secondary: PlusMenuItem[];
  /** The + button: focus goes back to it and it closes the focus loop. */
  toggleRef: RefObject<HTMLButtonElement | null>;
  /** The tab capsule's buttons: the bar the panel grows out of. */
  children: ReactNode;
}

const PRIMARY_COLUMNS = 2;
const SECONDARY_COLUMNS = 3;
/** A downward swipe longer than this on the open panel closes it. */
const SWIPE_CLOSE_PX = 64;
/** Movement before a press turns into a drag (keeps taps on the rows). */
const DRAG_SLOP_PX = 8;

/** The panel's padding (src/styles/plus-menu.css, .plus-panel). */
const PANEL_PADDING_PX = 10;

const ITEM_SELECTOR = '[role="menuitem"]';

const rows = (count: number, columns: number) => Math.ceil(count / columns);

const prefersReducedMotion = () =>
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/** Full-screen dim behind the open + panel; a tap on it closes the panel. */
export const PlusBackdrop = ({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) => (
  <div
    aria-hidden
    data-open={open ? "" : undefined}
    className="plus-backdrop"
    onClick={onClose}
  />
);

/**
 * The mobile tab bar capsule and the + quick-create panel it turns into: one
 * glass surface that holds the tabs (`children`) while closed and grows
 * upward, from the bar, into a card of create tiles and navigation chips.
 * Always mounted (the panel is inert while closed, the tabs while open) so
 * opening and closing are CSS transitions that reverse from wherever they
 * are when the + is tapped mid-way; a short WAAPI squeeze adds the gooey
 * pinch (see ./squash.ts and src/styles/plus-menu.css).
 */
export const PlusMenu = ({
  id,
  open,
  onClose,
  primary,
  secondary,
  toggleRef,
  children,
}: PlusMenuProps) => {
  const translate = useTranslate();
  const dockRef = useRef<HTMLDivElement>(null);
  const surfaceRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const squash = useRef<Animation | null>(null);
  const wasOpen = useRef(open);
  const drag = useRef<{ pointer: number; startY: number; dy: number } | null>(
    null,
  );
  const draggingRef = useRef(false);
  useTapThroughGuard(open);

  // The surface grows to the panel's measured height (CSS cannot transition
  // to `auto`); a panel taller than the screen allows scrolls inside.
  useLayoutEffect(() => {
    const panel = panelRef.current;
    const surface = surfaceRef.current;
    if (!panel || !surface) return;
    const measure = () => {
      surface.style.setProperty("--plus-h", `${panel.offsetHeight}px`);
      // Layout height, not scrollHeight: the hidden rows' entrance offset
      // would count as overflow.
      const last = panel.lastElementChild as HTMLElement | null;
      const natural = last
        ? last.offsetTop + last.offsetHeight + panel.clientTop
        : 0;
      panel.toggleAttribute(
        "data-scrollable",
        natural > panel.clientHeight - PANEL_PADDING_PX + 1,
      );
    };
    measure();
    if (typeof ResizeObserver !== "function") return;
    const observer = new ResizeObserver(measure);
    observer.observe(panel);
    for (const child of panel.children) observer.observe(child);
    return () => observer.disconnect();
  }, []);

  // The bar pinches in as it turns into the panel and as it takes it back,
  // and the + swells as it turns.
  useEffect(() => {
    if (wasOpen.current === open) return;
    wasOpen.current = open;
    if (prefersReducedMotion()) return;
    if (dockRef.current) {
      squash.current = playSquash(dockRef.current, open, squash.current);
    }
    if (open && toggleRef.current) playPop(toggleRef.current);
  }, [open, toggleRef]);

  useEffect(() => () => squash.current?.cancel(), []);

  // Focus enters the panel on open and goes back to the + on close (only if
  // it was inside, so a sheet opened by an action keeps it).
  useEffect(() => {
    const panel = panelRef.current;
    if (!panel) return;
    if (open) {
      panel
        .querySelector<HTMLElement>(ITEM_SELECTOR)
        ?.focus({ preventScroll: true });
      return;
    }
    if (panel.contains(document.activeElement)) {
      toggleRef.current?.focus({ preventScroll: true });
    }
  }, [open, toggleRef]);

  // Escape closes; Tab cycles through the rows and the + only.
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key !== "Tab") return;
      const loop = [
        ...(panelRef.current?.querySelectorAll<HTMLElement>(ITEM_SELECTOR) ??
          []),
        ...(toggleRef.current ? [toggleRef.current] : []),
      ];
      if (loop.length === 0) return;
      const index = loop.indexOf(document.activeElement as HTMLElement);
      const step = event.shiftKey ? -1 : 1;
      const next =
        index === -1
          ? event.shiftKey
            ? loop.length - 1
            : 0
          : (index + step + loop.length) % loop.length;
      event.preventDefault();
      loop[next].focus();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, onClose, toggleRef]);

  // A field taking focus raises the keyboard: the panel steps aside for good
  // instead of reappearing over the page once the keyboard goes down.
  useEffect(() => {
    if (!open) return;
    const onFocusIn = (event: FocusEvent) => {
      if (isEditableElement(event.target as Element | null)) onClose();
    };
    document.addEventListener("focusin", onFocusIn);
    return () => document.removeEventListener("focusin", onFocusIn);
  }, [open, onClose]);

  // Arrow keys move between rows like a native menu.
  const onMenuKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    const items = [
      ...event.currentTarget.querySelectorAll<HTMLElement>(ITEM_SELECTOR),
    ];
    const index = items.indexOf(document.activeElement as HTMLElement);
    const last = items.length - 1;
    const target = {
      ArrowDown: index + 1 > last ? 0 : index + 1,
      ArrowRight: index + 1 > last ? 0 : index + 1,
      ArrowUp: index - 1 < 0 ? last : index - 1,
      ArrowLeft: index - 1 < 0 ? last : index - 1,
      Home: 0,
      End: last,
    }[event.key];
    if (target === undefined) return;
    event.preventDefault();
    items[target]?.focus();
  };

  // The swipe pushes the panel back down into the bar as the finger moves.
  const setDrag = (dy: number | null) => {
    const panel = panelRef.current;
    const surface = surfaceRef.current;
    if (!panel || !surface) return;
    if (dy == null) {
      panel.removeAttribute("data-dragging");
      surface.style.removeProperty("--plus-drag");
    } else {
      panel.setAttribute("data-dragging", "");
      surface.style.setProperty("--plus-drag", `${dy}px`);
    }
  };

  const onPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!open || !event.isPrimary) return;
    drag.current = { pointer: event.pointerId, startY: event.clientY, dy: 0 };
    draggingRef.current = false;
  };

  const onPointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    const current = drag.current;
    if (!current || current.pointer !== event.pointerId) return;
    const dy = event.clientY - current.startY;
    if (!draggingRef.current) {
      if (Math.abs(dy) < DRAG_SLOP_PX) return;
      draggingRef.current = true;
      try {
        event.currentTarget.setPointerCapture(event.pointerId);
      } catch {
        // The pointer can already be gone (lifted, cancelled): drag anyway.
      }
    }
    // Follows the finger down; resists going up.
    current.dy = dy > 0 ? dy : dy / 5;
    setDrag(current.dy);
  };

  const endDrag = (event: ReactPointerEvent<HTMLDivElement>) => {
    const current = drag.current;
    if (!current || current.pointer !== event.pointerId) return;
    drag.current = null;
    if (!draggingRef.current) return;
    setDrag(null);
    if (current.dy > SWIPE_CLOSE_PX) onClose();
  };

  // A drag that started on a row must not also activate it.
  const onClickCapture = (event: ReactMouseEvent) => {
    if (!draggingRef.current) return;
    draggingRef.current = false;
    event.preventDefault();
    event.stopPropagation();
  };

  const choose = (item: PlusMenuItem) => () => {
    hapticTick();
    onClose();
    if ("run" in item) item.run();
  };

  const primaryRows = rows(primary.length, PRIMARY_COLUMNS);
  const secondaryRows = rows(secondary.length, SECONDARY_COLUMNS);
  // Rows from the bottom: chips, their label, tiles, then the top label.
  const secondaryLabelOrder = secondaryRows;
  const primaryBase = secondaryRows + 0.6;
  const primaryLabelOrder = primaryBase + primaryRows;

  const renderItem = (
    item: PlusMenuItem,
    className: string,
    content: ReactNode,
  ) =>
    "to" in item ? (
      <Link
        to={item.to}
        role="menuitem"
        onClick={choose(item)}
        className={className}
      >
        {content}
      </Link>
    ) : (
      <button
        type="button"
        role="menuitem"
        onClick={choose(item)}
        className={className}
      >
        {content}
      </button>
    );

  return (
    <div
      ref={dockRef}
      data-open={open ? "" : undefined}
      className="plus-dock relative h-[var(--mobile-pill)] shrink-0"
    >
      <div ref={surfaceRef} className="plus-surface liquid-glass">
        <div
          ref={panelRef}
          id={id}
          role="menu"
          aria-label={translate("crm.mobile.quick_actions")}
          aria-orientation="vertical"
          aria-hidden={open ? undefined : true}
          inert={!open}
          data-open={open ? "" : undefined}
          className="plus-panel"
          onKeyDown={onMenuKeyDown}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
          onClickCapture={onClickCapture}
        >
          <div role="group" aria-label={translate("crm.mobile.create")}>
            <div
              aria-hidden
              className="plus-item plus-section-label"
              style={orderStyle(primaryLabelOrder)}
            >
              {translate("crm.mobile.create")}
            </div>
            <div className="grid grid-cols-2 gap-2">
              {primary.map((item, index) => (
                <div
                  key={item.label}
                  role="none"
                  className="plus-item"
                  style={orderStyle(
                    primaryBase +
                      staggerOrder(index, PRIMARY_COLUMNS, primary.length),
                  )}
                >
                  {renderItem(
                    item,
                    "plus-press plus-tile flex h-[3.25rem] w-full items-center gap-2 rounded-[1.125rem] pr-1.5 pl-1.5 text-left outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50",
                    <>
                      <span className="plus-tile-icon flex size-[2.125rem] shrink-0 items-center justify-center rounded-full">
                        <item.Icon
                          className="size-[1.2rem]"
                          strokeWidth={2.2}
                        />
                      </span>
                      <span className="line-clamp-2 min-w-0 text-[0.8125rem] leading-[1.15] font-semibold tracking-[-0.01em]">
                        {item.label}
                      </span>
                    </>,
                  )}
                </div>
              ))}
            </div>
          </div>
          <div
            role="group"
            aria-label={translate("crm.mobile.plus.go_to")}
            className="mt-2.5"
          >
            <div
              aria-hidden
              className="plus-item plus-section-label"
              style={orderStyle(secondaryLabelOrder)}
            >
              {translate("crm.mobile.plus.go_to")}
            </div>
            <div className="grid grid-cols-3 gap-1.5">
              {secondary.map((item, index) => (
                <div
                  key={item.label}
                  role="none"
                  className="plus-item"
                  style={orderStyle(
                    staggerOrder(index, SECONDARY_COLUMNS, secondary.length),
                  )}
                >
                  {renderItem(
                    item,
                    "plus-press plus-chip flex h-[3.25rem] w-full flex-col items-center justify-center gap-1 rounded-[1.125rem] px-0.5 outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50",
                    <>
                      <item.Icon className="size-[1.125rem]" strokeWidth={2} />
                      <span className="max-w-full truncate text-[0.6875rem] leading-tight font-medium tracking-[-0.015em]">
                        {item.label}
                      </span>
                    </>,
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      <div
        data-slot="tab-capsule"
        inert={open}
        className="plus-tabs relative flex h-full items-stretch gap-0.5 px-1 py-0.5"
      >
        {children}
      </div>
    </div>
  );
};

const orderStyle = (order: number): CSSProperties =>
  ({ "--i": Number(order.toFixed(2)) }) as CSSProperties;
